import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { 
  Save, Eye, ArrowLeft, Image, X, Tag, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  "Digital PR & Media Coverage",
  "Social Media Marketing",
  "Print Media & Advertising",
  "Multi-Channel PR Strategies",
  "Founder Visibility & Personal Branding",
  "PR for Startups & Corporates"
];

const AdminBlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    category: CATEGORIES[0],
    tags: [],
    status: 'draft',
    meta_title: '',
    meta_description: ''
  });

  useEffect(() => {
    if (isEdit) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    const token = localStorage.getItem('token');
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const blog = response.data;
      setFormData({
        title: blog.title || '',
        slug: blog.slug || '',
        excerpt: blog.excerpt || '',
        content: blog.content || '',
        featured_image: blog.featured_image || '',
        category: blog.category || CATEGORIES[0],
        tags: blog.tags || [],
        status: blog.status || 'draft',
        meta_title: blog.meta_title || '',
        meta_description: blog.meta_description || ''
      });
    } catch (error) {
      console.error('Error fetching blog:', error);
      toast.error('Failed to load blog');
      navigate('/admin/blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem('token');
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, uploadData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, featured_image: response.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e, status = formData.status) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.excerpt.trim()) {
      toast.error('Excerpt is required');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Content is required');
      return;
    }

    const token = localStorage.getItem('token');
    setSaving(true);

    try {
      const payload = {
        ...formData,
        status
      };

      if (isEdit) {
        await axios.put(`${API}/admin/blogs/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Blog updated successfully');
      } else {
        await axios.post(`${API}/admin/blogs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Blog created successfully');
      }
      
      navigate('/admin/blogs');
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error('Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 w-48 bg-gray-200 rounded mb-8"></div>
            <div className="h-12 w-full bg-gray-200 rounded mb-4"></div>
            <div className="h-32 w-full bg-gray-200 rounded mb-4"></div>
            <div className="h-64 w-full bg-gray-200 rounded"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/blogs')}
              className="p-2 hover:bg-[#f5f5f5] rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-[#737373]" />
            </button>
            <div>
              <h1 className="font-playfair text-2xl font-bold text-[#1a1a1a]">
                {isEdit ? 'Edit Article' : 'New Article'}
              </h1>
              <p className="font-manrope text-sm text-[#737373]">
                {isEdit ? 'Update your blog post' : 'Create a new insight article'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => handleSubmit(e, 'draft')}
              disabled={saving}
              className="px-4 py-2 border border-[#e5e5e5] rounded-lg font-manrope text-sm hover:bg-[#f5f5f5] transition-colors disabled:opacity-50"
            >
              Save Draft
            </button>
            <button
              onClick={(e) => handleSubmit(e, 'published')}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg font-manrope text-sm hover:bg-[#B8960F] transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Publish'}
            </button>
          </div>
        </div>

        <form onSubmit={(e) => handleSubmit(e)} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block font-manrope text-sm font-medium text-[#1a1a1a] mb-2">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter article title..."
              className="w-full px-4 py-3 border border-[#e5e5e5] rounded-lg font-manrope focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block font-manrope text-sm font-medium text-[#1a1a1a] mb-2">
              URL Slug
            </label>
            <input
              type="text"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              placeholder="auto-generated-from-title"
              className="w-full px-4 py-3 border border-[#e5e5e5] rounded-lg font-manrope focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
            />
            <p className="mt-1 text-xs text-[#737373]">Leave blank to auto-generate from title</p>
          </div>

          {/* Category */}
          <div>
            <label className="block font-manrope text-sm font-medium text-[#1a1a1a] mb-2">
              Category *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-[#e5e5e5] rounded-lg font-manrope focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Featured Image */}
          <div>
            <label className="block font-manrope text-sm font-medium text-[#1a1a1a] mb-2">
              Featured Image
            </label>
            {formData.featured_image ? (
              <div className="relative inline-block">
                <img 
                  src={formData.featured_image.startsWith('/') ? `${BACKEND_URL}${formData.featured_image}` : formData.featured_image}
                  alt="Featured"
                  className="w-64 h-40 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, featured_image: '' }))}
                  className="absolute -top-2 -right-2 p-1 bg-[#dc2626] text-white rounded-full"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-64 h-40 border-2 border-dashed border-[#e5e5e5] rounded-lg cursor-pointer hover:border-[#D4AF37] transition-colors">
                <Image className="w-8 h-8 text-[#737373] mb-2" />
                <span className="font-manrope text-sm text-[#737373]">Upload image</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Excerpt */}
          <div>
            <label className="block font-manrope text-sm font-medium text-[#1a1a1a] mb-2">
              Excerpt * <span className="text-[#737373] font-normal">(max 300 characters)</span>
            </label>
            <textarea
              name="excerpt"
              value={formData.excerpt}
              onChange={handleChange}
              placeholder="Brief summary of the article..."
              maxLength={300}
              rows={3}
              className="w-full px-4 py-3 border border-[#e5e5e5] rounded-lg font-manrope focus:ring-2 focus:ring-[#D4AF37] focus:outline-none resize-none"
            />
            <p className="mt-1 text-xs text-[#737373]">{formData.excerpt.length}/300 characters</p>
          </div>

          {/* Content */}
          <div>
            <label className="block font-manrope text-sm font-medium text-[#1a1a1a] mb-2">
              Content * <span className="text-[#737373] font-normal">(HTML supported)</span>
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Write your article content here... HTML tags like <h2>, <p>, <ul>, <li>, <strong>, <a> are supported."
              rows={15}
              className="w-full px-4 py-3 border border-[#e5e5e5] rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#D4AF37] focus:outline-none resize-none"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block font-manrope text-sm font-medium text-[#1a1a1a] mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.tags.map((tag, idx) => (
                <span 
                  key={idx}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-[#f5f5f5] text-[#737373] rounded-full text-sm"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-[#dc2626]"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Add a tag..."
                className="flex-1 px-4 py-2 border border-[#e5e5e5] rounded-lg font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 border border-[#e5e5e5] rounded-lg hover:bg-[#f5f5f5] transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* SEO Section */}
          <div className="border-t border-[#e5e5e5] pt-6">
            <h3 className="font-playfair text-lg font-bold text-[#1a1a1a] mb-4">SEO Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block font-manrope text-sm font-medium text-[#1a1a1a] mb-2">
                  Meta Title
                </label>
                <input
                  type="text"
                  name="meta_title"
                  value={formData.meta_title}
                  onChange={handleChange}
                  placeholder="Leave blank to use article title"
                  className="w-full px-4 py-3 border border-[#e5e5e5] rounded-lg font-manrope focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block font-manrope text-sm font-medium text-[#1a1a1a] mb-2">
                  Meta Description
                </label>
                <textarea
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleChange}
                  placeholder="Leave blank to use excerpt"
                  rows={2}
                  className="w-full px-4 py-3 border border-[#e5e5e5] rounded-lg font-manrope focus:ring-2 focus:ring-[#D4AF37] focus:outline-none resize-none"
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminBlogForm;
